var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import EventEmitter from 'eventemitter3';
import { DEFAULT_FETCH_POLICY, DEFAULT_REQUEST_METHOD } from '../constants';
import { ApiError } from '../errors';
import { GenericCache } from '../generic-cache';
import { applyHeaders, createSubscription, getParamsId } from '../lib';
import { ApiRequestFetcher } from '../request-fetcher';
function identity(value) {
    return value;
}
const RECEIVED_RESPONSE_BODY_EVENT = 'received_response_body';
const ERROR_EVENT = 'error';
/**
 * The request manager is responsible for managing API requests.
 *
 * Responsibilities include:
 * - Implementing `getRequestBody` which is called by `Api`
 * - Caching in-progress GET requests
 * - Detecting a JSON request body by stringifying and setting the appropriate Content-Type headers
 * - Serializing / parsing JSON requests & responses
 */
export class ApiRequestManager {
    constructor(params) {
        this.emitter = new EventEmitter();
        this.defaultHeaders = new Headers();
        this.inProgressRequestCache = new GenericCache();
        /**
         * Returns a promise that resolves with the response body.
         *
         * If there is an in-progress request, and `options.deduplicate` is `true`,
         * it will return the same promise.
         *
         * On a successful fetch, it will cache the response unless `fetchPolicy` is `'no-cache'`
         */
        this.getResponseBody = (params, options) => {
            var _a;
            const paramsKey = getParamsId(params);
            // return cached promise if it exists
            const cachedRequest = this.inProgressRequestCache.get(paramsKey);
            const deduplicate = (_a = options.deduplicate, (_a !== null && _a !== void 0 ? _a : defaultDeduplicate(params)));
            if (cachedRequest && deduplicate) {
                // if user hasn't explicitly requested a new fetch,
                // return the in-progress fetch promise
                return cachedRequest.requestPromise;
            }
            const id = Symbol();
            const fetchPromise = this.createRequestPromise(params, options, id);
            this.inProgressRequestCache.set(paramsKey, {
                id,
                requestPromise: fetchPromise
            });
            return fetchPromise;
        };
        /**
         * Configuring an error handler to be called on error
         */
        this.onReceivedResponseBody = (listener) => {
            return createSubscription(this.emitter, RECEIVED_RESPONSE_BODY_EVENT, listener);
        };
        /**
         * Configuring an error handler to be called on error
         */
        this.onError = (listener) => {
            return createSubscription(this.emitter, ERROR_EVENT, listener);
        };
        /**
         * Returns `true` if a `GET` request matches params
         */
        this.requestInProgress = (params) => {
            return this.inProgressRequestCache.has(getParamsId(params));
        };
        /**
         * Set default headers to be passed to all API requests.
         * Useful for setting an authentication token.
         *
         * @param key Header key
         * @param value Header value
         */
        this.setDefaultHeader = (key, value) => {
            this.defaultHeaders.set(key, value);
        };
        this.fetchResponseBody = (params, options) => __awaiter(this, void 0, void 0, function* () {
            const { method = DEFAULT_REQUEST_METHOD } = params;
            const { fetchPolicy = DEFAULT_FETCH_POLICY } = options;
            try {
                let headers = new Headers(this.defaultHeaders);
                if (params.headers) {
                    headers = applyHeaders(headers, params.headers);
                }
                let body;
                if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
                    const paramBody = params.body;
                    if (paramBody &&
                        ![Blob, FormData, URLSearchParams, ReadableStream].some((bodyType) => paramBody instanceof bodyType) &&
                        typeof paramBody !== 'string') {
                        // prepare JSON body and add header
                        body = JSON.stringify(this.serializeRequestJson(paramBody));
                        headers.set('Content-Type', 'application/json');
                    }
                    else {
                        body = paramBody;
                    }
                }
                const response = yield this.requestFetcher.getResponse({
                    method,
                    url: `${this.baseUrl}${params.url}`,
                    body,
                    headers,
                    responseType: params.responseType
                });
                const responseBody = response.responseType === 'json'
                    ? this.parseResponseJson(response.responseBody)
                    : response.responseBody;
                if (fetchPolicy !== 'no-cache') {
                    this.emitter.emit(RECEIVED_RESPONSE_BODY_EVENT, params, responseBody);
                }
                return responseBody;
            }
            catch (error) {
                /* istanbul ignore next */
                if (error instanceof ApiError && error.responseType === 'json') {
                    error.responseBody = this.parseResponseJson(error.responseBody);
                }
                this.emitter.emit(ERROR_EVENT, error);
                throw error;
            }
        });
        this.baseUrl = params.baseUrl || '';
        this.requestFetcher = params.requestFetcher || new ApiRequestFetcher();
        this.serializeRequestJson = params.serializeRequestJson || identity;
        this.parseResponseJson = params.parseResponseJson || identity;
    }
    createRequestPromise(params, options, id) {
        return __awaiter(this, void 0, void 0, function* () {
            const paramsId = getParamsId(params);
            try {
                return yield this.fetchResponseBody(params, options);
            }
            finally {
                const cachedRequest = this.inProgressRequestCache.get(paramsId);
                if (cachedRequest && cachedRequest.id === id) {
                    this.inProgressRequestCache.del(paramsId);
                }
            }
        });
    }
}
function defaultDeduplicate(params) {
    return params.method === 'GET' ? true : false;
}
//# sourceMappingURL=index.js.map